import React, { useState } from 'react';
import classnames from 'classnames';
import Modal, {
  Header,
  Title,
  Body,
  Footer,
} from '@aha-app/react-bootstrap-modal';
import styles from '../Dashboards.module.css';
import ReportPicker from './ReportPicker';
import PanelLibrary from './PanelLibrary';
import ExtensionLibrary from './ExtensionLibrary';
import {
  analyticsType,
  CONTEXT_DEFAULT_VIEW_SETTINGS,
  contextToName,
  VIEW_EXISTING_REPORTS,
  VIEW_EXTENSION_PANELS,
  VIEW_PANEL_LIBRARY,
  CONTEXT_PDLC_KEY_VIEW,
} from '../helpers';
import AhaAnalytics from 'javascripts/analytics';
import {
  DASHBOARD_PANEL_CONTEXT_DASHBOARD,
  DASHBOARD_PANEL_CONTEXT_NOTE,
  DASHBOARD_PANEL_CONTEXT_WHITEBOARD,
} from '../constants';

/**
 * @typedef NewPanelModalProps
 * @prop {*=} open
 * @prop {*=} closePanelModal
 * @prop {*=} addPanelFromExistingReport
 * @prop {*=} addExtensionPanel
 * @prop {*=} addRichTextPanel
 * @prop {*=} addPanelFromExampleReport
 * @prop {*=} examples
 * @prop {*=} panelCount
 * @prop {*=} maxPanels
 * @prop {*=} showExtensions
 * @prop {*=} context
 * @prop {*=} defaultView
 * @prop {*=} defaultSelectedReportType
 * @prop {*=} enableCopying
 * @prop {*=} showBackButton
 */
/**
 * @type {React.FC<NewPanelModalProps>}
 */
const NewPanelModal = ({
  open,
  closePanelModal,
  addPanelFromExistingReport,
  addExtensionPanel,
  addRichTextPanel,
  addPanelFromExampleReport,
  examples,
  panelCount,
  maxPanels,
  showExtensions,
  context,
  defaultView = VIEW_PANEL_LIBRARY,
  defaultSelectedReportType = 'all',
  enableCopying = true,
  showBackButton = true,
}) => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [currentView, setCurrentView] = useState(defaultView);
  const [selectedReportType, setSelectedReportType] = useState(
    defaultSelectedReportType
  );
  const [useOriginal, setUseOriginal] = useState(false);

  let body;
  switch (currentView) {
    case VIEW_PANEL_LIBRARY:
      body = (
        <PanelLibrary
          addRichTextPanel={addRichTextPanel}
          selectedReport={selectedReport}
          setSelectedReport={setSelectedReport}
          selectedReportType={selectedReportType}
          setSelectedReportType={setSelectedReportType}
          setCurrentView={setCurrentView}
          examplePanels={examples ? examples.toJS() : []}
          showExtensions={showExtensions}
          context={context}
        />
      );
      break;

    case VIEW_EXTENSION_PANELS:
      body = (
        <ExtensionLibrary
          selectedReport={selectedReport}
          setSelectedReport={setSelectedReport}
          selectedReportType={selectedReportType}
          setSelectedReportType={setSelectedReportType}
          setCurrentView={setCurrentView}
          context={context}
        />
      );

      break;

    default:
      body = (
        <ReportPicker
          defaultSelectedReportType={defaultSelectedReportType}
          selectedReport={selectedReport}
          setSelectedReport={setSelectedReport}
          selectedReportType={selectedReportType}
          setSelectedReportType={setSelectedReportType}
          context={context}
        />
      );
      break;
  }

  const createNewPanel = selectedReport => {
    if (context === DASHBOARD_PANEL_CONTEXT_NOTE) {
      AhaAnalytics.track(
        'Feature-6741102573941417068',
        'User clicks add view button from modal',
        { 'View type': analyticsType(selectedReport, currentView) }
      );
    }
    switch (currentView) {
      case VIEW_EXISTING_REPORTS:
        addPanelFromExistingReport(selectedReport);
        break;

      case VIEW_EXTENSION_PANELS:
        addExtensionPanel(selectedReport);
        break;

      default:
        addPanelFromExampleReport(selectedReport);
        break;
    }
  };

  // Handle going back from existing reports to panel library
  const handleBackBtnClick = () => {
    setCurrentView(VIEW_PANEL_LIBRARY);
    if (['list', 'pivot', 'chart', 'roadmap'].indexOf(selectedReportType) < 0)
      setSelectedReportType('all');
    setSelectedReport(null);
  };
  const disableAddBtn =
    !selectedReport || !selectedReport.id || panelCount >= maxPanels;
  const tooltipProps = {};
  const message = `A dashboard can have a maximum of ${maxPanels} panels. You must remove a panel in order to add a new one.`;
  if (panelCount >= maxPanels) {
    tooltipProps['data-tooltip'] = message;
  }

  let ctaText = 'Add to dashboard';
  if (context === DASHBOARD_PANEL_CONTEXT_NOTE) {
    ctaText = 'Add to note';
  } else if (context === DASHBOARD_PANEL_CONTEXT_WHITEBOARD) {
    ctaText = 'Add to whiteboard';
  } else if (context === CONTEXT_DEFAULT_VIEW_SETTINGS) {
    ctaText = 'Set default view';
  } else if (context === CONTEXT_PDLC_KEY_VIEW) {
    ctaText = 'Add to resources tab';
  }

  let modalTitle = `Add new ${contextToName(context)}`;
  if (context === CONTEXT_DEFAULT_VIEW_SETTINGS) {
    modalTitle = 'Set default view';
  } else if (context === CONTEXT_PDLC_KEY_VIEW) {
    modalTitle = 'Link to view';
  }

  return (
    <Modal
      show={open}
      onHide={closePanelModal}
      className={classnames(styles.Modal, 'modal-big')}
      dialogClassName={styles.ModalDialogFix}
      backdrop={'static'}
    >
      <Header closeButton>
        <Title className={styles.ModalTitle}>{modalTitle}</Title>
      </Header>
      <Body className={styles.ModalBody}>{body}</Body>
      <Footer className={styles.ModalFooter}>
        {showBackButton &&
          [VIEW_EXISTING_REPORTS, VIEW_EXTENSION_PANELS].includes(
            currentView
          ) && (
            <div
              className='back-btn btn btn-default'
              onClick={handleBackBtnClick}
            >
              {'\u2190 Back'}
            </div>
          )}
        {enableCopying && selectedReport && parseInt(selectedReport.id) > 0 && (
          <label className={styles.copyBox}>
            <span data-popover data-content-ref data-placement='bottom'>
              <script type='text/template'>
                A copy of this view will be created that can only be edited from
                the{' '}
                {context === DASHBOARD_PANEL_CONTEXT_NOTE ||
                context === DASHBOARD_PANEL_CONTEXT_WHITEBOARD
                  ? context
                  : DASHBOARD_PANEL_CONTEXT_DASHBOARD}
                . Uncheck if you would like to use the original view.
              </script>
              Create a copy
            </span>
            <input
              id='make-a-copy'
              type='checkbox'
              checked={!useOriginal}
              onChange={() => setUseOriginal(!useOriginal)}
            />
          </label>
        )}
        <button
          disabled={disableAddBtn}
          title={panelCount >= maxPanels ? message : ''}
          className={classnames(
            {
              disabled: disableAddBtn,
            },
            'btn',
            'btn-primary'
          )}
          {...tooltipProps}
          onClick={() =>
            createNewPanel({ useOriginal: useOriginal, ...selectedReport })
          }
        >
          {ctaText}
        </button>
      </Footer>
    </Modal>
  );
};

export default NewPanelModal;
